import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { siteConfig, navigationLinks } from '../../data/mock';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b-2 border-[#1E3A8A]'
          : 'bg-white border-b-2 border-[#1E3A8A]'
      }`}
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4">
            <img
              src={siteConfig.logo}
              alt={siteConfig.name}
              className="h-16 w-16 object-contain"
            />
            <div>
              <span className="text-[#1E3A8A] font-black text-xl font-['Merriweather']">{siteConfig.name}</span>
              <span className="block text-[#E31837] text-sm font-bold tracking-wider uppercase">
                {siteConfig.tagline}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {navigationLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-5 py-2 text-base font-bold transition-all duration-300 border-b-2 ${
                  isActive(link.path)
                    ? 'text-[#E31837] border-[#E31837]'
                    : 'text-[#1E3A8A] border-transparent hover:text-[#E31837] hover:border-[#E31837]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-6">
            <a
              href={`tel:${siteConfig.phone.replace(/[^0-9]/g, '')}`}
              className="flex items-center gap-2 text-[#1E3A8A] hover:text-[#E31837] transition-colors text-base font-bold"
            >
              <Phone className="w-5 h-5" />
              <span>{siteConfig.phone}</span>
            </a>
            <Link to="/apply">
              <Button className="btn-primary font-bold">
                Start Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-[#1E3A8A]">
                <Menu className="h-7 w-7" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white border-l-2 border-[#1E3A8A] w-80">
              <div className="flex flex-col h-full pt-8">
                <div className="flex items-center gap-3 mb-10">
                  <img
                    src={siteConfig.logo}
                    alt={siteConfig.name}
                    className="h-14 w-14 object-contain"
                  />
                  <div>
                    <span className="text-[#1E3A8A] font-black text-lg">{siteConfig.name}</span>
                    <span className="block text-[#E31837] text-xs font-bold">{siteConfig.tagline}</span>
                  </div>
                </div>

                <nav className="flex flex-col gap-2">
                  {navigationLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className={`px-4 py-3 text-lg font-bold transition-all border-l-4 ${
                        isActive(link.path)
                          ? 'bg-gray-50 text-[#E31837] border-[#E31837]'
                          : 'text-[#1E3A8A] border-transparent hover:bg-gray-50 hover:border-[#1E3A8A]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </nav>

                <div className="mt-auto pb-8">
                  <a
                    href={`tel:${siteConfig.phone.replace(/[^0-9]/g, '')}`}
                    className="flex items-center gap-2 text-[#1E3A8A] mb-6 px-4 text-lg font-bold"
                  >
                    <Phone className="w-5 h-5" />
                    <span>{siteConfig.phone}</span>
                  </a>
                  <Link to="/apply" onClick={() => setMobileOpen(false)}>
                    <Button className="btn-primary w-full text-lg font-bold">
                      Start Your Transformation
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

export default Header;
